import { useEffect, useState } from "react";
import { useAtom } from "jotai";

import { videoPlayerAtom } from "@/constants/atoms";

import { StreamingIcons } from "../Icon";
import { IconButton } from "../IconButton";
import { ChromecastButtonProps } from "./types";

/** Google Cast SDK type definitions (inline for proper TypeScript resolution) */
declare global {
  interface Window {
    __onGCastApiAvailable?: (isAvailable: boolean) => void;
    cast?: typeof cast;
  }
}

declare namespace cast {
  namespace framework {
    class CastContext {
      static getInstance(): CastContext;
      setOptions(options: any): void;
      requestSession(): Promise<CastSession>;
      getCurrentSession(): CastSession | null;
      endCurrentSession(stopCasting: boolean): void;
      getCastState(): CastState;
      addEventListener(
        type: CastContextEventType,
        handler: (event: any) => void,
      ): void;
    }
    interface CastSession {
      loadMedia(request: any): Promise<void>;
    }
    enum CastContextEventType {
      CAST_STATE_CHANGED = "caststatechanged",
      SESSION_STATE_CHANGED = "sessionstatechanged",
    }
    enum CastState {
      NO_DEVICES_AVAILABLE = "NO_DEVICES_AVAILABLE",
      NOT_CONNECTED = "NOT_CONNECTED",
      CONNECTING = "CONNECTING",
      CONNECTED = "CONNECTED",
    }
    enum SessionState {
      NO_SESSION = "NO_SESSION",
      SESSION_STARTED = "SESSION_STARTED",
      SESSION_RESUMED = "SESSION_RESUMED",
    }
  }
}

declare namespace chrome {
  namespace cast {
    enum AutoJoinPolicy {
      ORIGIN_SCOPED = "origin_scoped",
    }
    namespace media {
      const DEFAULT_MEDIA_RECEIVER_APP_ID: string;
    }
  }
}

/**
 * Chromecast button for video player controls
 * Works on Chrome browsers with Chromecast support
 * Uses videoPlayerAtom to access video element and video source
 *
 * @param props - The button properties
 * @returns the Chromecast button component
 */
const ChromecastButton = ({ ariaLabel, className }: ChromecastButtonProps) => {
  const [videoPlayerState, setVideoPlayerState] = useAtom(videoPlayerAtom);
  const [isCastAvailable, setIsCastAvailable] = useState(false);

  useEffect(() => {
    console.info("ChromecastButton mounted");

    /** Set up the callback before the SDK loads */
    window.__onGCastApiAvailable = (isAvailable) => {
      console.info(
        "__onGCastApiAvailable callback fired, isAvailable:",
        isAvailable,
      );
      if (isAvailable) {
        initializeCastApi();
      }
    };

    /** Check if SDK is already loaded */
    if (window.cast?.framework) {
      console.info("Cast SDK already loaded, initializing...");
      initializeCastApi();
    }

    return () => {
      console.info("ChromecastButton unmounting");
      window.__onGCastApiAvailable = undefined;
    };
  }, []);
  const initializeCastApi = () => {
    /** The Cast SDK places chrome.cast directly on window, not window.chrome */
    const chromeCast = (window as any).chrome?.cast;
    const castFramework = window.cast?.framework;

    /** Debug: Log what's actually available */
    console.info("Checking Cast SDK availability:", {
      hasCast: !!window.cast,
      hasCastFramework: !!castFramework,
      hasWindowChrome: !!(window as any).chrome,
      hasChromeCast: !!chromeCast,
      /** Check if chrome.cast is on window directly */
      hasWindowChromeDotCast: !!(window as any)["chrome"]?.["cast"],
    });

    if (!castFramework) {
      console.warn("Cast framework not available");
      return;
    }

    try {
      /** Initialize cast context - the framework will handle chrome.cast internally */
      const castContext = cast.framework.CastContext.getInstance();

      /** Use the Default Media Receiver App ID (this is a constant in the SDK) */
      castContext.setOptions({
        receiverApplicationId: "CC1AD845" /** Default Media Receiver */,
        autoJoinPolicy: "origin_scoped" as any,
      });

      console.info("Cast context initialized successfully", {
        receiverAppId: chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });

      /** Mark as available initially (will be updated by cast state listener) */
      setIsCastAvailable(true);

      /** Listen for cast state changes (receiver availability) */
      castContext.addEventListener(
        cast.framework.CastContextEventType.CAST_STATE_CHANGED,
        (event: any) => {
          const castState = event.castState;
          console.info("Cast state changed:", {
            castState,
            eventType: "CAST_STATE_CHANGED",
            rawEvent: event,
          });

          /** Update availability based on cast state */
          /** Cast states: NO_DEVICES_AVAILABLE, NOT_CONNECTED, CONNECTING, CONNECTED */
          const isAvailable =
            castState !== cast.framework.CastState.NO_DEVICES_AVAILABLE;

          console.info("Updating Cast availability:", {
            previousAvailable: isCastAvailable,
            newAvailable: isAvailable,
            castState,
          });

          setIsCastAvailable(isAvailable);
        },
      );

      /** Listen for session state changes */
      castContext.addEventListener(
        cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        (event: any) => {
          const session = event.sessionState;
          const isConnected =
            session === cast.framework.SessionState.SESSION_STARTED ||
            session === cast.framework.SessionState.SESSION_RESUMED;
          setVideoPlayerState((prev) => ({
            ...prev,
            isCastConnected: isConnected,
          }));
        },
      );

      /** Check initial session state */
      const currentSession = castContext.getCurrentSession();
      setVideoPlayerState((prev) => ({
        ...prev,
        isCastConnected: !!currentSession,
      }));

      /** Check initial cast state for device availability */
      const initialCastState = castContext.getCastState();
      const isAvailable =
        initialCastState !== cast.framework.CastState.NO_DEVICES_AVAILABLE;

      console.info("Initial cast state check:", {
        initialCastState,
        isAvailable,
        NO_DEVICES_AVAILABLE: cast.framework.CastState.NO_DEVICES_AVAILABLE,
        NOT_CONNECTED: cast.framework.CastState.NOT_CONNECTED,
        CONNECTING: cast.framework.CastState.CONNECTING,
        CONNECTED: cast.framework.CastState.CONNECTED,
      });

      setIsCastAvailable(isAvailable);
    } catch (error) {
      console.error("Error initializing Cast API:", error);
    }
  };

  const handleClick = () => {
    const castFramework = window.cast?.framework;

    console.info("Chromecast button clicked", {
      isCastAvailable,
      hasCastFramework: !!castFramework,
    });

    if (!castFramework) {
      console.warn("Cast framework not available - SDK not loaded yet");
      return;
    }

    if (!isCastAvailable) {
      console.warn("No Cast devices available on the network");
      return;
    }

    try {
      const castContext = cast.framework.CastContext.getInstance();
      const castSession = castContext.getCurrentSession();

      if (castSession) {
        /** Already connected, stop casting */
        console.info("Ending current Cast session");
        castContext.endCurrentSession(true);
        setVideoPlayerState((prev) => ({ ...prev, isCastConnected: false }));
      } else {
        /** Request a cast session */
        console.info("Requesting new Cast session");
        castContext
          .requestSession()
          .then(() => {
            console.info("Cast session started successfully");
            const session = castContext.getCurrentSession();
            if (session && videoPlayerState.videoRef?.current) {
              loadMedia(session);
              setVideoPlayerState((prev) => ({
                ...prev,
                isCastConnected: true,
              }));
            } else {
              console.warn("Cast session started but no video element found");
            }
          })
          .catch((error: any) => {
            console.error("Error requesting cast session:", error);
            setVideoPlayerState((prev) => ({
              ...prev,
              isCastConnected: false,
            }));
          });
      }
    } catch (error) {
      console.error("Error in handleClick:", error);
    }
  };

  const loadMedia = (session: cast.framework.CastSession) => {
    const videoElement = videoPlayerState.videoRef?.current as HTMLVideoElement;

    if (!videoElement || !videoElement.src) {
      console.warn("Cannot load media: missing video element", {
        hasVideoElement: !!videoElement,
        hasVideoSrc: !!videoElement?.src,
      });
      return;
    }

    console.info("Loading media to Cast device", {
      src: videoElement.src,
      currentTime: videoElement.currentTime,
      paused: videoElement.paused,
    });

    /** Use the framework's simplified media info structure */
    const mediaInfo = {
      contentId: videoElement.src,
      contentType: "video/mp4",
      streamType: "BUFFERED",
      metadata: {
        type: 0 /** GENERIC */,
        metadataType: 0,
        title: document.title || "Video",
      },
    };

    const request = {
      media: mediaInfo,
      currentTime: videoElement.currentTime,
      autoplay: !videoElement.paused,
    };

    session
      .loadMedia(request)
      .then(() => {
        console.info("Media loaded successfully");
      })
      .catch((error: any) => {
        console.error("Error loading media:", error);
      });
  };

  /** During development, show button even if no devices are available (for debugging) */
  /** In production, hide button if Chromecast is not available */
  const isDevelopment = import.meta.env.DEV;

  if (!isCastAvailable && !isDevelopment) {
    return null;
  }

  /** Show visual feedback when no devices available */
  const noDevices = !isCastAvailable;

  return (
    <IconButton
      icon={
        videoPlayerState.isCastConnected
          ? StreamingIcons.ChromecastConnected
          : StreamingIcons.Chromecast
      }
      onClick={handleClick}
      disabled={noDevices}
      ariaLabel={
        ariaLabel ||
        (noDevices
          ? "Chromecast (no devices found)"
          : videoPlayerState.isCastConnected
            ? "Disconnect Chromecast"
            : "Connect to Chromecast")
      }
      className={className}
    />
  );
};

export { ChromecastButton };
